import { faBusinessTime, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Center,
  Container,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { IState } from '../../data/reducers/state';
import { IAssociation } from '../../types/association/Association';
import AssociationPayrateAttacher from './AssociationPayrateAttacher';
import PayrateManager from './PayrateManager';
import PayratesViewer from './PayratesViewer';

interface PayrateManagementProps {
  associations: IAssociation[];
  selectedAssociation?: IAssociation | null;
}

const PayrateManagement: React.FC<PayrateManagementProps> = ({
  associations,
  selectedAssociation,
}) => {
  const [activeTab, setActiveTab] = useState<string>('view');
  const [loading, setLoading] = useState(true);
  const [effectiveDate, setEffectiveDate] = useState<DateTime>(DateTime.local().startOf('day'));

  useEffect(() => {
    setLoading(false);
  }, [associations]);

  const resolvedAssociation = selectedAssociation ?? null;

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (associations.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Alert color="blue" title="No Associations Available">
          You don't have manager access to any associations.
          Contact your system administrator to get manager privileges for an association.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack spacing="xl">
        <div>
          <Title order={1} mb="sm">Payrate Management</Title>
          <Text color="dimmed" size="lg">
            Create and manage custom payrates for your associations
          </Text>
        </div>

        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'view')}>
          <Tabs.List>
            <Tabs.Tab
              value="manage"
              leftSection={<FontAwesomeIcon icon={faDollarSign} size="sm" />}
            >
              Payrates
            </Tabs.Tab>
            <Tabs.Tab
              value="view"
              leftSection={<FontAwesomeIcon icon={faBusinessTime} size="sm" />}
            >
              Payrate rules
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="manage" pt="md">
            <Stack gap="lg">
              <Alert color="blue" title="Payrate Management">
                <Text size="sm">
                  Create custom payrates for your associations. System payrates are read-only
                  templates that can be cloned. Association payrates can be edited and deleted.
                </Text>
              </Alert>
              <PayrateManager />
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="view" pt="md">
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <div>
                  <Title order={4} style={{ marginBottom: 4 }}>
                    Association Payrates
                  </Title>
                  <Text size="sm" color="dimmed">
                    {resolvedAssociation
                      ? `Viewing payrates for ${resolvedAssociation.name}`
                      : 'Select an association from the admin header to view payrates.'}
                  </Text>
                </div>
                <AssociationPayrateAttacher
                  variant="button"
                  selectedAssociation={resolvedAssociation || undefined}
                  effectiveDate={effectiveDate}
                  onEffectiveDateChange={(date) => {
                    if (!date) {
                      setEffectiveDate(DateTime.local().startOf('day'));
                      return;
                    }
                    const dt = date instanceof Date ? DateTime.fromJSDate(date).startOf('day') : DateTime.fromISO(String(date)).startOf('day');
                    if (dt?.isValid) setEffectiveDate(dt);
                  }}
                />
              </Group>
              <Alert title="Payrate Viewer" color="blue">
                <Text size="sm">
                  View all available payrates and their priority order for the selected association.
                  This shows how payrates cascade and which rates will be applied to games.
                  <br /><br />
                  Higher priority payrates override lower priority ones.
                </Text>
              </Alert>
              {resolvedAssociation ? (
                <PayratesViewer
                  selectedAssociation={resolvedAssociation}
                  effectiveDate={effectiveDate}
                  onEffectiveDateChange={(value) => {
                    if (!value) {
                      setEffectiveDate(DateTime.local().startOf('day'));
                      return;
                    }
                    const dt = value instanceof Date ? DateTime.fromJSDate(value).startOf('day') : DateTime.fromISO(String(value)).startOf('day');
                    if (dt?.isValid) setEffectiveDate(dt);
                  }}
                />
              ) : (
                <Alert title="No Association Selected">
                  Select an association from the admin header to view payrates.
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};

const mapStateToProps = (state: IState) => ({
  associations: state.associations.admin.map(
    (a) => state.associations.map[a],
  ).filter(Boolean),
});

export default connect(mapStateToProps)(PayrateManagement);
